// ── Day Planner Engine ──

import type { ErlangCInputs, DayPlannerInterval, DayPlannerResult, DayPlannerSummary } from '../types/erlang';
import { findMinAgentsC, findMinAgentsA, serviceLevel, asa, erlangACompute, serviceLevelA } from './erlangEngine';

export const DEFAULT_INTERVALS: DayPlannerInterval[] = [
  { time: '08:00', calls: 120 },
  { time: '08:30', calls: 180 },
  { time: '09:00', calls: 280 },
  { time: '09:30', calls: 350 },
  { time: '10:00', calls: 380 },
  { time: '10:30', calls: 360 },
  { time: '11:00', calls: 340 },
  { time: '11:30', calls: 310 },
  { time: '12:00', calls: 250 },
  { time: '12:30', calls: 230 },
  { time: '13:00', calls: 260 },
  { time: '13:30', calls: 290 },
  { time: '14:00', calls: 320 },
  { time: '14:30', calls: 340 },
  { time: '15:00', calls: 330 },
  { time: '15:30', calls: 300 },
  { time: '16:00', calls: 270 },
  { time: '16:30', calls: 220 },
  { time: '17:00', calls: 180 },
  { time: '17:30', calls: 140 },
  { time: '18:00', calls: 100 },
  { time: '18:30', calls: 70 },
  { time: '19:00', calls: 50 },
  { time: '19:30', calls: 30 },
];

export function calculateDayPlan(
  intervals: DayPlannerInterval[],
  baseInputs: ErlangCInputs,
  useErlangA: boolean = false,
  avgPatienceTime: number = 120
): DayPlannerSummary {
  const {
    ahtSeconds,
    intervalMinutes,
    targetSLPercent,
    targetAnswerTime,
    shrinkagePercent,
    costPerAgentHour,
  } = baseInputs;

  const intervalSeconds = intervalMinutes * 60;
  const targetSL = targetSLPercent / 100;
  const shrinkage = shrinkagePercent / 100;

  const results: DayPlannerResult[] = intervals.map(({ time, calls }) => {
    if (calls <= 0) {
      return { time, calls, trafficIntensity: 0, rawAgents: 0, rosteredAgents: 0, serviceLevel: 1, asa: 0, occupancy: 0 };
    }

    const traffic = (calls * ahtSeconds) / intervalSeconds;
    let rawAgents: number;

    if (useErlangA) {
      rawAgents = findMinAgentsA(traffic, targetSL, targetAnswerTime, ahtSeconds, avgPatienceTime);
    } else {
      rawAgents = findMinAgentsC(traffic, targetSL, targetAnswerTime, ahtSeconds);
    }

    const rosteredAgents = Math.ceil(rawAgents / (1 - shrinkage));

    let sl: number, asaVal: number;
    let carried = traffic; // load actually handled (abandoners are never handled)
    if (useErlangA) {
      sl = serviceLevelA(rawAgents, traffic, ahtSeconds, avgPatienceTime, targetAnswerTime);
      const aResult = erlangACompute(rawAgents, traffic, ahtSeconds, avgPatienceTime);
      asaVal = aResult.avgWait;
      carried = traffic * (1 - aResult.abandonRate);
    } else {
      sl = serviceLevel(rawAgents, traffic, targetAnswerTime, ahtSeconds);
      asaVal = asa(rawAgents, traffic, ahtSeconds);
    }

    const occ = rawAgents > 0 ? carried / rawAgents : 0;

    return { time, calls, trafficIntensity: traffic, rawAgents, rosteredAgents, serviceLevel: sl, asa: asaVal, occupancy: occ };
  });

  const peakRaw = Math.max(...results.map((r) => r.rawAgents));
  const peakRostered = Math.max(...results.map((r) => r.rosteredAgents));
  const avgRaw = results.reduce((s, r) => s + r.rawAgents, 0) / results.length;
  const totalAgentHours = results.reduce((s, r) => s + r.rosteredAgents * (intervalMinutes / 60), 0);
  const totalCost = totalAgentHours * costPerAgentHour;

  return {
    peakRawAgents: peakRaw,
    peakRosteredAgents: peakRostered,
    avgRawAgents: avgRaw,
    totalAgentHours,
    totalCost,
    intervals: results,
  };
}
